import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

import { useSession } from '@platform/api-client/react';
import { Alert, Card, PageHeader, Spinner, useToast } from '@platform/ui';

import { tenantOptionsKey } from '../../features/tenants/use-tenant';
import { InvitePlatformUserDialog } from '../../features/users/invite-platform-user-dialog';
import { PendingPromotionsPanel } from '../../features/users/pending-promotions-panel';
import { UserDrawer } from '../../features/users/user-drawer';
import {
  DEFAULT_USER_FILTERS,
  UsersDirectory,
  type UserFilters,
} from '../../features/users/users-directory';
import {
  PLATFORM_ENDPOINT_PERMISSIONS,
  type InvitePlatformUserRequest,
  type PendingPromotionRow,
  type PlatformUserSummary,
} from '../../lib/api/platform';
import { usePlatformClient } from '../../lib/api/platform-client-context';
import { RequirePermission } from '../../lib/session/guards';
import { PLATFORM_PERMISSIONS, usePermissions } from '../../lib/session/permissions';
import { useStepUp } from '../../lib/session/step-up';

export const Route = createFileRoute('/users/')({
  component: PlatformUsersRoute,
});

/**
 * `/users` (FR-SUPER-03, MASTER_PLAN M12.3, `Docs/API.md` §2.13).
 *
 * The route owns every call and the screen owns none, which is the split the rest of this
 * console uses (rules §17.1, §17.8). Two things are specific to this screen:
 *
 * - **Every write goes through `useStepUp().guard()`.** Resetting a second factor,
 *   revoking sessions and changing a platform role are `@StepUp()` endpoints; the guard
 *   catches the refusal, asks for a code, and runs the original call again with the
 *   reason the operator already typed (SECURITY.md §4.3, §6.2).
 * - **The operator's own id comes from the session, not from the directory.** "You cannot
 *   act on your own account" has to be decided from the real signed-in session, because
 *   the directory is data and the session is identity.
 * - **The promotions queue is read only when the operator may act on it.**
 *   `GET /platform/promotions` is gated on `platform.superadmin.promote`, so the query is
 *   `enabled` on that key: a `platform_support` account makes no request and sees no
 *   panel, rather than an empty panel or a `403` on a screen they are entitled to.
 */
function PlatformUsersRoute() {
  return (
    <RequirePermission keys={[PLATFORM_PERMISSIONS.usersRead]}>
      <PlatformUsersPage />
    </RequirePermission>
  );
}

function PlatformUsersPage() {
  const client = usePlatformClient();
  const queryClient = useQueryClient();
  const session = useSession();
  const { can } = usePermissions();
  const { guard } = useStepUp();
  const { toast } = useToast();

  const [filters, setFilters] = useState<UserFilters>(DEFAULT_USER_FILTERS);
  const [openUserId, setOpenUserId] = useState<string | null>(null);
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState<string | undefined>(undefined);
  const [actionError, setActionError] = useState<string | undefined>(undefined);
  const [promotionError, setPromotionError] = useState<string | undefined>(undefined);
  const [busyPromotionId, setBusyPromotionId] = useState<string | null>(null);

  const users = useInfiniteQuery({
    queryKey: ['platform-users', filters],
    queryFn: ({ pageParam }) => client.listUsers({ ...filters, limit: 50, cursor: pageParam }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.page.nextCursor,
  });
  const tenants = useQuery({
    queryKey: tenantOptionsKey(),
    queryFn: async () => (await client.listTenants({ limit: 100 })).data,
  });
  const detail = useQuery({
    queryKey: ['platform-user', openUserId],
    queryFn: () => client.getUser(openUserId ?? ''),
    enabled: openUserId !== null,
  });

  const canManage = can(PLATFORM_ENDPOINT_PERMISSIONS.usersManage);
  const canPromote = can(PLATFORM_ENDPOINT_PERMISSIONS.superadminPromote);
  const currentUserId = session.data?.user?.id ?? null;

  const promotions = useQuery({
    queryKey: ['platform-promotions', 'pending'],
    queryFn: () => client.listPendingPromotions({ status: 'pending' }),
    enabled: canPromote,
  });

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['platform-users'] });
    await queryClient.invalidateQueries({ queryKey: ['platform-user'] });
    /* A promotion requested or confirmed changes the queue as well as the account. */
    await queryClient.invalidateQueries({ queryKey: ['platform-promotions'] });
  };

  /** Every drawer action lands here: step-up, refresh, toast, or show the refusal. */
  const act = useMutation({
    mutationFn: ({ run }: { run: () => Promise<unknown>; message: string; action: string }) =>
      guard(run, 'act on this account'),
    onSuccess: async (_result, variables) => {
      setActionError(undefined);
      toast({ tone: 'success', message: variables.message });
      await refresh();
    },
    onError: (error: Error) => {
      setActionError(error.message);
    },
  });

  /*
   * The second approval. It is the same `confirmPromotion` the seam has always carried;
   * what was missing was somewhere to find the request. Step-up gated like every other
   * write on this screen, and the queue is re-read after it succeeds so the row leaves.
   */
  const confirm = useMutation({
    mutationFn: (promotion: PendingPromotionRow) => {
      setBusyPromotionId(promotion.id);
      return guard(
        () => client.confirmPromotion(promotion.targetUserId, { promotionId: promotion.id }),
        'confirm a platform administrator',
      );
    },
    onSuccess: async ({ user }) => {
      setBusyPromotionId(null);
      setPromotionError(undefined);
      toast({ tone: 'success', message: `${user.email} is now a platform administrator.` });
      await refresh();
    },
    onError: (error: Error) => {
      setBusyPromotionId(null);
      setPromotionError(error.message);
    },
  });

  const invite = useMutation({
    mutationFn: (input: InvitePlatformUserRequest) => client.invitePlatformUser(input),
    onSuccess: async (user) => {
      setInviteError(undefined);
      setInviting(false);
      toast({ tone: 'success', message: `Invitation sent to ${user.email}.` });
      await refresh();
    },
    onError: (error: Error) => {
      setInviteError(error.message);
    },
  });

  if (users.isPending || tenants.isPending) {
    return (
      <>
        <PageHeader title="Users" />
        <Card className="p-6">
          <Spinner label="Loading users" />
        </Card>
      </>
    );
  }

  if (users.isError || tenants.isError) {
    return (
      <>
        <PageHeader title="Users" />
        <Alert tone="error">The user directory could not be loaded. Try again in a moment.</Alert>
      </>
    );
  }

  const rows: PlatformUserSummary[] = users.data.pages.flatMap((page) => page.data);
  const openUser: PlatformUserSummary | null = rows.find((user) => user.id === openUserId) ?? null;
  const targetId = openUser?.id ?? openUserId ?? '';

  return (
    <>
      <UsersDirectory
        users={rows}
        hasMore={users.hasNextPage}
        loadingMore={users.isFetchingNextPage}
        onLoadMore={() => {
          void users.fetchNextPage();
        }}
        tenants={tenants.data.map((tenant) => ({ id: tenant.id, name: tenant.name }))}
        filters={filters}
        onFiltersChange={setFilters}
        canManage={canManage}
        onOpen={(user) => {
          setActionError(undefined);
          setOpenUserId(user.id);
        }}
        onInvite={() => {
          setInviteError(undefined);
          setInviting(true);
        }}
        promotions={
          canPromote ? (
            <PendingPromotionsPanel
              promotions={promotions.data?.data ?? []}
              pending={promotions.isPending}
              currentUserId={currentUserId}
              busyPromotionId={busyPromotionId}
              error={
                promotions.isError ? 'Outstanding promotions could not be loaded.' : promotionError
              }
              onConfirm={(promotion) => {
                confirm.mutate(promotion);
              }}
            />
          ) : undefined
        }
        drawer={
          <UserDrawer
            open={openUserId !== null}
            onClose={() => {
              setOpenUserId(null);
            }}
            detail={detail.data ?? null}
            pending={detail.isPending && openUserId !== null}
            error={actionError}
            isSelf={currentUserId !== null && currentUserId === targetId}
            canManage={canManage}
            canPromote={canPromote}
            onRevokeSessions={() => {
              act.mutate({
                run: () => client.revokeUserSessions(targetId),
                message: 'Every session was revoked.',
                action: 'revoke sessions',
              });
            }}
            onResetMfa={(reason) => {
              act.mutate({
                run: () => client.resetUserMfa(targetId, { reason }),
                message: 'Two-factor was reset. They will enrol again at their next sign-in.',
                action: 'reset two-factor',
              });
            }}
            onSetActive={(active, reason) => {
              act.mutate({
                run: () => client.setUserActive(targetId, { active, reason }),
                message: active ? 'The account was reactivated.' : 'The account was deactivated.',
                action: active ? 'reactivate an account' : 'deactivate an account',
              });
            }}
            /*
             * `confirmation` is the address the operator typed, sent on to the server:
             * the phrase is not only a gate on the button, it is what
             * `POST /platform/users/:id/promote` checks (API.md §2.13). A promotion to
             * `platform_admin` answers `pending`, because a second administrator has to
             * confirm it - the toast says so rather than implying it is done.
             */
            onPromote={(reason, confirmation) => {
              act.mutate({
                run: () =>
                  client.promoteUser(targetId, {
                    role: 'platform_admin',
                    reason,
                    confirmation,
                  }),
                message:
                  'Requested. A second platform administrator has to confirm before the role is granted.',
                action: 'promote a platform administrator',
              });
            }}
            onDemote={(reason, confirmation) => {
              act.mutate({
                run: () => client.demoteUser(targetId, { reason, confirmation }),
                message: 'The platform role was removed.',
                action: 'remove a platform role',
              });
            }}
          />
        }
      />

      <InvitePlatformUserDialog
        open={inviting}
        error={inviteError}
        onClose={() => {
          setInviting(false);
        }}
        onInvite={(input) => {
          invite.mutate(input);
        }}
      />
    </>
  );
}
